import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import pLimit, { type LimitFunction } from "p-limit";

declare module "axios" {
	interface InternalAxiosRequestConfig {
		/**
		 * @internal Used by the concurrency limiter interceptors.
		 */
		__releaseConcurrencySlot?: () => void
	}
}

/**
 * Per-axios-instance concurrency limiter. Interceptors look up the active concurrency limit here on each request.
 * A WeakMap so state is dropped when an instance is no longer referenced (tests, extra clients).
 */
const concurrencyLimiterByInstance = new WeakMap<AxiosInstance, LimitFunction>();

/**
 * Configures concurrent-request limiting for an axios instance.
 *
 * @remarks
 * - Disabling the limiter is handled at the call site.
 * - Intended to be called **once** per instance (from {@link AjaxConfigRegistration} in `created()`).
 *   A second call throws (duplicate interceptors would otherwise stall requests).
 * - Aborted or cancelled requests are not removed from the p-limit queue (no fail-fast). A request
 *   that is aborted while waiting for a slot still blocks the queue until it acquires one; axios
 *   then rejects it as usual once the request actually starts.
 *
 * @param concurrencyLimit
 * Maximum number of concurrent in-flight requests. Must be `>= 1`.
 *
 * @param axiosInstance
 * The axios instance to configure. Defaults to the shared singleton instance.
 */
export function setupConcurrencyLimit(concurrencyLimit: number, axiosInstance: AxiosInstance = axios): void {
	if (concurrencyLimit < 1) {
		throw new Error("concurrencyLimit must be greater than 0");
	}

	if (concurrencyLimiterByInstance.has(axiosInstance)) {
		throw new Error("setupConcurrencyLimit was already called for this axios instance");
	}

	concurrencyLimiterByInstance.set(axiosInstance, pLimit(concurrencyLimit));
	registerInterceptors(axiosInstance);
};

/**
 * Releases this request's concurrency limit (p-limit) slot, if held. Idempotent.
 */
export function releaseConcurrencySlot(config?: InternalAxiosRequestConfig): void {
	if (config == null) {
		return;
	}

	const release = config.__releaseConcurrencySlot;
	if (release == null) {
		return;
	}

	config.__releaseConcurrencySlot = undefined;
	release();
};

/**
 * Waits for a concurrency limit (p-limit) slot, then keeps it until {@link releaseConcurrencySlot}.
 */
function acquireConcurrencySlot(config: InternalAxiosRequestConfig, limiter: LimitFunction): Promise<void> {
	return new Promise<void>((resolve) => {
		void limiter(() => new Promise<void>((release) => {
			// p-limit keeps this task active until `release()` runs. The callback is stored on
			// the config so response/error/abort handlers can free the slot after the request ends.
			config.__releaseConcurrencySlot = release;

			// slot acquired; unblock the request interceptor while the slot stays held
			resolve();
		}));
	});
};

/**
 * Registers request/response interceptors that acquire and release p-limit slots.
 */
function registerInterceptors(axiosInstance: AxiosInstance): void {
	axiosInstance.interceptors.request.use(async (config) => {
		if (config.skipConcurrencyLimiter) {
			return config;
		}

		const concurrencyLimiter = concurrencyLimiterByInstance.get(axiosInstance);
		if (!concurrencyLimiter) {
			return config;
		}

		await acquireConcurrencySlot(config, concurrencyLimiter);
		return config;
	});

	axiosInstance.interceptors.response.use(
		(response) => {
			releaseConcurrencySlot(response.config);
			return response;
		},
		(error) => {
			releaseConcurrencySlot(error.config);
			return Promise.reject(error);
		},
	);
};
